import { EHydraCreditNoteState, EHydraCreditNoteVoidState, EThirdParty } from "../entity-interfaces/EHydraCreditNoteState";
import { IBillingAddress } from "../../../rentsync-api/invoicing/IBillingAddress";
import { ICredit } from "./ICredit";
import { Nullable, PartialOn } from "@jamesgmarks/utilities";
import { Payment, PdfGeneration } from "../../../entities/hydra";
import { IBillingAccount } from "../entity-interfaces/IBillingAccount";
import { IOwnershipGroups } from "@llws/lift-entity-interfaces";
import { INoteComment } from "../../../entities/jsonTypes/INoteComment";

/**
 * Represents a single application of a credit note against an invoice.
 * Tracks the applied amounts, the target invoice, and the lifecycle state
 * of the application within the Hydra billing system.
 */
export interface ICreditApplied {
  id?: number;
  invoiceId?: Nullable<number>;
  amount: Nullable<string>;
  taxAmount: Nullable<string>;
  dateApplied?: Nullable<Date | string>;
  hydraState: Nullable<string>;
  invoice?: {
    invoiceNumber?: Nullable<string>;
  };
}

export interface ICreditNote {
  readonly id?: number;
  billingAccountId?: number;
  billingAddress: Nullable<IBillingAddress>;
  clientId?: number,
  credits: ICredit[];
  currencyCode: string;
  thirdPartyCreditNoteId?: Nullable<string>;
  thirdParty: Nullable<EThirdParty>;
  noteDate: string;
  noteNumber?: string,
  notes: Nullable<INoteComment[]>;
  ownershipGroupId?: Nullable<number>;
  pdfGenerations: PdfGeneration[];
  state: EHydraCreditNoteState;
  voidState: Nullable<EHydraCreditNoteVoidState>;
  voidRequestReason: Nullable<string>;
  voidApproveOrDenyReason: Nullable<string>;
  subtotalAmount: Nullable<string>;
  taxAmount: Nullable<string>;
  /**
   * Optional adjustment (up to 5 decimal places) that records the difference between the
   * posted (cents-rounded) credit note total and the raw sum of the child credit lines.
   * A positive value means the posted total is slightly higher than the raw total; a
   * negative value means it is slightly lower. Stored as a decimal string.
   */
  roundingAdjustment?: Nullable<string>;
  taxName: Nullable<string>;
  taxRate: Nullable<string>;
  creditsApplied?: ICreditApplied[];
}

export interface IAugmentedCreditNote extends ICreditNote {
  id: number;
  billingAccount: IBillingAccount;
  ownershipGroup?: IOwnershipGroups;
}

export interface ICreditNoteWithPayments extends ICreditNote {
  payments: Payment[];
}

export type TCreditNoteRequiredProperties = (
  'id' | 'noteNumber' | 'billingAccountId' | 'currencyCode' | 'state' | 'noteDate' | 'credits'
);
export type TNewCreditNote = PartialOn<ICreditNote, TCreditNoteRequiredProperties>;

/** Type guard for whether an object is a Credit Note. */
export const isICreditNote = (object: unknown): object is ICreditNote => !!(object as ICreditNote).noteDate;

/** Type guard for whether an array contains only `ICreditNote`s. */
export const isICreditNoteArray = (object: unknown[]): object is ICreditNote[] => object.every(isICreditNote);

export const assertICreditNoteArray = (arr: unknown[]): ICreditNote[] | never => {
  if (!isICreditNoteArray(arr)) {
    throw new Error('This is not an array of `ICreditNote`!');
  }

  return arr;
};

/** Type guard for whether an object is a Credit Note. */
export const isIAugmentedCreditNote = (object: unknown): object is IAugmentedCreditNote => (
  !!(object as ICreditNote).noteDate && !!(object as IAugmentedCreditNote).billingAccount
);

/** Type guard for whether an array contains only `IAugmentedCreditNote`s. */
export const isIAugmentedCreditNoteArray = (object: unknown[]): object is IAugmentedCreditNote[] => (
  object.every(isIAugmentedCreditNote)
);

export const assertIAugmentedCreditNoteArray = (arr: unknown[]): IAugmentedCreditNote[] | never => {
  if (!isIAugmentedCreditNoteArray(arr)) {
    throw new Error('This is not an array of `IAugmentedCreditNote`!');
  }

  return arr;
};
